import { useEffect } from "react";
import { useRouterState } from "@tanstack/react-router";

/**
 * Globale scroll-reveal — elementen zweven op vanuit onderen wanneer ze
 * in beeld komen. Gebruikt GSAP ScrollTrigger en volgt Lenis smooth-scroll.
 *
 * Selectie:
 *  - Elk element met `data-reveal` (opt-in),
 *  - én directe kinderen van elke <section>/<article> in de body,
 *    behalve als het element (of een voorouder) `nav`, `footer` of
 *    `[data-no-reveal]` is.
 *
 * Binnen één container animeren kinderen met een lichte stagger, zodat
 * kaarten/teksten in golfjes van onderen opkomen.
 *
 * Herstart bij elke route-wissel zodat nieuwe DOM meteen mee-animeert.
 */
export function RevealRoot() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    let disposed = false;

    const boot = async () => {
      const [{ gsap }, { ScrollTrigger }] = await Promise.all([
        import("gsap"),
        import("gsap/ScrollTrigger"),
      ]);
      if (disposed) return;
      gsap.registerPlugin(ScrollTrigger);

      const isExcluded = (el: Element) =>
        el.closest("nav, footer, [data-no-reveal]") !== null;

      const groups = new Map<HTMLElement, HTMLElement[]>();

      const addToGroup = (el: HTMLElement) => {
        if (isExcluded(el)) return;
        const parent = el.parentElement as HTMLElement | null;
        if (!parent) return;
        const list = groups.get(parent) ?? [];
        list.push(el);
        groups.set(parent, list);
      };

      document
        .querySelectorAll<HTMLElement>("[data-reveal]")
        .forEach((el) => addToGroup(el));

      document
        .querySelectorAll<HTMLElement>("main section, main article, section, article")
        .forEach((container) => {
          if (isExcluded(container)) return;
          Array.from(container.children).forEach((child) => {
            if (!(child instanceof HTMLElement)) return;
            if (isExcluded(child)) return;
            if (child.hasAttribute("data-no-reveal")) return;
            addToGroup(child);
          });
        });

      const viewportH = window.innerHeight;

      const ctx = gsap.context(() => {
        groups.forEach((elements) => {
          // Verberg enkel elementen die onder de viewport beginnen;
          // elementen die al zichtbaar zijn bij mount blijven staan.
          const hidden = elements.filter((el) => {
            const rect = el.getBoundingClientRect();
            return rect.top >= viewportH * 0.95;
          });
          if (hidden.length === 0) return;

          gsap.fromTo(
            hidden,
            { opacity: 0, y: 64, scale: 0.98 },
            {
              opacity: 1,
              y: 0,
              scale: 1,
              duration: 0.85,
              ease: "power3.out",
              stagger: 0.08,
              immediateRender: false,
              scrollTrigger: {
                trigger: hidden[0],
                start: "top 88%",
                toggleActions: "play none none none",
                once: true,
              },
            },
          );
        });
      });

      ScrollTrigger.refresh();

      (window as unknown as { __revealCtx?: gsap.Context }).__revealCtx = ctx;
    };

    const raf = requestAnimationFrame(() => void boot());

    return () => {
      disposed = true;
      cancelAnimationFrame(raf);
      const w = window as unknown as { __revealCtx?: gsap.Context };
      w.__revealCtx?.revert();
      w.__revealCtx = undefined;
    };
  }, [pathname]);

  return null;
}
